import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Row,
  Col,
  Statistic,
  Descriptions,
  Tabs,
  Select,
  Typography
} from 'antd';
import {
  LinkOutlined,
  EditOutlined,
  ReloadOutlined,
  EyeOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;

const adminApi = axios.create({ baseURL: '/api' });
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const { Option } = Select;
const { TabPane } = Tabs;

const CallbackConfig = () => {
  const [loading, setLoading] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [callbackLogs, setCallbackLogs] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [logSearch, setLogSearch] = useState('');
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [form] = Form.useForm();
  const [logsPagination, setLogsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [settlementConfig, setSettlementConfig] = useState(null);
  const [settlementModalVisible, setSettlementModalVisible] = useState(false);
  const [settlementForm] = Form.useForm();
  const [pendingSettlements, setPendingSettlements] = useState([]);
  const [resendLoading, setResendLoading] = useState(null);

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    setMerchantsLoading(true);
    try {
      const response = await adminApi.get('/users?role=merchant');
      setMerchants(response.data.users || response.data || []);
    } catch (error) {
      message.error('Gagal memuat daftar merchant');
      console.error('Fetch merchants error:', error);
    } finally {
      setMerchantsLoading(false);
    }
  };

  const fetchCallbackConfig = async (memberId) => {
    setLoading(true);
    try {
      const callbackRes = await adminApi.get(`/member-management/callback-config/${memberId}`);
      setSelectedConfig(callbackRes.data);
      setSelectedMemberId(memberId);
    } catch (error) {
      message.error('Gagal memuat konfigurasi callback');
      console.error('Fetch callback config error:', error);
    } finally {
      setLoading(false);
    }

    // Fetch settlement config & pending (tidak blokir callback display)
    try {
      const settlementRes = await adminApi.get(`/member-management/settlement-config/${memberId}`);
      setSettlementConfig(settlementRes.data);
    } catch (e) {
      console.error('Fetch settlement config error:', e);
      setSettlementConfig({ settlement_delay_hours: 0 });
    }

    try {
      const pendingRes = await adminApi.get(`/member-management/pending-settlements/${memberId}`);
      setPendingSettlements(pendingRes.data.data || []);
    } catch (e) {
      console.error('Fetch pending settlements error:', e);
      setPendingSettlements([]);
    }
  };

  const handleSettlementEdit = () => {
    settlementForm.setFieldsValue({ settlement_delay_hours: settlementConfig?.settlement_delay_hours ?? 0 });
    setSettlementModalVisible(true);
  };

  const handleSettlementSubmit = async (values) => {
    try {
      await adminApi.put(`/member-management/settlement-config/${selectedMemberId}`, values);
      message.success('Settlement delay berhasil diperbarui');
      setSettlementModalVisible(false);
      fetchCallbackConfig(selectedMemberId);
    } catch (error) {
      message.error('Gagal memperbarui settlement delay');
    }
  };

  const fetchCallbackLogs = async (memberId, page = 1, search = logSearch) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: logsPagination.pageSize
      };
      // Dicari di transaction_id maupun isi payload, sehingga ref ID mitra
      // (external_id) di dalam callback batch ADV-... ikut ketemu.
      if (search) params.search = search;

      const response = await adminApi.get(`/member-management/callback-logs/${memberId}`, { params });
      setCallbackLogs(response.data.callback_logs);
      setLogsPagination(prev => ({
        ...prev,
        current: page,
        total: response.data.pagination.totalItems
      }));
    } catch (error) {
      message.error('Gagal memuat log callback');
      console.error('Fetch callback logs error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    form.setFieldsValue({
      ...config,
      callback_enabled: config.callback_enabled === 1,
      use_signature: config.use_signature === 1,
      email_notifications: config.email_notifications === 1,
      sms_notifications: config.sms_notifications === 1,
      settlement_delay_hours: settlementConfig ? settlementConfig.settlement_delay_hours : 0
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const { settlement_delay_hours, ...callbackValues } = values;
      await adminApi.put(`/member-management/callback-config/${selectedMemberId}`, callbackValues);
      await adminApi.put(`/member-management/settlement-config/${selectedMemberId}`, { settlement_delay_hours: settlement_delay_hours ?? 0 });
      message.success('Konfigurasi berhasil diperbarui');
      setModalVisible(false);
      fetchCallbackConfig(selectedMemberId);
    } catch (error) {
      message.error('Gagal memperbarui konfigurasi');
      console.error('Update config error:', error);
    }
  };

  const handleViewLogs = (memberId) => {
    setSelectedMemberId(memberId);
    setLogSearch('');
    fetchCallbackLogs(memberId, 1, '');
    setLogsModalVisible(true);
  };

  const handleResendCallback = async (record) => {
    setResendLoading(record.id);
    try {
      const url = record.transaction_id?.startsWith('ADV-')
        ? `/transactions/callback-logs/${record.id}/resend`
        : `/transactions/${record.transaction_id}/resend-callback`;
      const res = await adminApi.post(url);
      if (res.data.success) {
        message.success(`Callback berhasil dikirim (HTTP ${res.data.http_status})`);
      } else {
        message.warning(`Callback gagal (HTTP ${res.data.http_status}): ${res.data.response}`);
      }
      if (selectedMemberId) fetchCallbackLogs(selectedMemberId, logsPagination.current);
    } catch (err) {
      message.error(err.response?.data?.error || 'Gagal mengirim callback');
    } finally {
      setResendLoading(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'orange';
      case 'retrying':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success':
        return 'Berhasil';
      case 'failed':
        return 'Gagal';
      case 'pending':
        return 'Pending';
      case 'retrying':
        return 'Mencoba Ulang';
      default:
        return status;
    }
  };

  const logColumns = [
    {
      title: 'Waktu',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 180,
      render: (date) => new Date(date).toLocaleString('id-ID'),
    },
    {
      title: 'Tipe',
      dataIndex: 'callback_type',
      key: 'callback_type',
      width: 120,
      render: (type) => (
        <Tag color="blue">
          {type === 'payment' ? 'Payment' : type === 'settlement' ? 'Settlement' : 'Refund'}
        </Tag>
      ),
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 200,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Response Code',
      dataIndex: 'response_code',
      key: 'response_code',
      width: 120,
    },
    {
      title: 'Attempt',
      dataIndex: 'attempt_number',
      key: 'attempt_number',
      width: 80,
    },
    {
      title: 'Response Time (ms)',
      dataIndex: 'response_time_ms',
      key: 'response_time_ms',
      width: 140,
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 120,
      render: (_, record) => (record.callback_type === 'payment') ? (
        <Button
          type="primary"
          size="small"
          icon={<SendOutlined />}
          loading={resendLoading === record.id}
          onClick={() => handleResendCallback(record)}
        >
          Resend
        </Button>
      ) : <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>Callback Configuration</h1>
        <p style={{ color: '#666' }}>Kelola konfigurasi webhook callback untuk member</p>
      </div>

      {/* Merchant Selection */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={18} md={20}>
            <Select
              showSearch
              placeholder="Pilih merchant untuk melihat konfigurasi callback..."
              style={{ width: '100%' }}
              loading={merchantsLoading}
              value={selectedMemberId}
              onChange={(value) => {
                setSelectedMemberId(value);
                fetchCallbackConfig(value);
              }}
              filterOption={(input, option) =>
                option.label?.toLowerCase().includes(input.toLowerCase())
              }
              options={merchants.map((m) => ({
                value: m.id,
                label: `${m.full_name || m.username} — ${m.email}`,
              }))}
              optionRender={(option) => (
                <div>
                  <div style={{ fontWeight: 500 }}>{option.data.label.split(' — ')[0]}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{option.data.label.split(' — ')[1]}</div>
                </div>
              )}
            />
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => selectedMemberId && fetchCallbackConfig(selectedMemberId)}
              disabled={!selectedMemberId}
              style={{ width: '100%' }}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Configuration Display */}
      {selectedConfig && (
        <div>
          {/* Stats Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Callback Status"
                  value={selectedConfig.callback_enabled ? 'Enabled' : 'Disabled'}
                  prefix={selectedConfig.callback_enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  valueStyle={{ color: selectedConfig.callback_enabled ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Max Retry"
                  value={selectedConfig.max_retry_attempts}
                  prefix={<HistoryOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Retry Interval"
                  value={selectedConfig.retry_interval}
                  suffix="sec"
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Timeout"
                  value={selectedConfig.timeout_seconds}
                  suffix="sec"
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Settlement Delay Card */}
          <Card style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0 }}>Settlement Period</h3>
                <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
                  Jeda waktu sebelum dana cash-in masuk ke saldo tersedia
                </p>
              </div>
              <Button type="primary" icon={<EditOutlined />} onClick={handleSettlementEdit}>
                Edit
              </Button>
            </div>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Delay"
                  value={settlementConfig ? settlementConfig.settlement_delay_hours : 0}
                  suffix="jam"
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: settlementConfig && settlementConfig.settlement_delay_hours > 0 ? '#fa8c16' : '#3f8600' }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Mode"
                  value={
                    !settlementConfig || settlementConfig.settlement_delay_hours === 0
                      ? 'Real-time'
                      : 'H+' + Math.floor(settlementConfig.settlement_delay_hours / 24) + ' (' + settlementConfig.settlement_delay_hours + ' jam)'
                  }
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Pending Settlement"
                  value={pendingSettlements.filter(function(p) { return p.status === 'pending'; }).length}
                  suffix="transaksi"
                />
              </Col>
            </Row>
          </Card>

          <Tabs defaultActiveKey="config">
            <TabPane tab="Konfigurasi" key="config">
              <Card>
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
                  <h3>Detail Konfigurasi Callback</h3>
                  <Space>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(selectedConfig)}
                    >
                      Edit Config
                    </Button>
                    <Button
                      icon={<HistoryOutlined />}
                      onClick={() => handleViewLogs(selectedMemberId)}
                    >
                      Lihat Log
                    </Button>
                  </Space>
                </div>

                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Member ID">{selectedConfig.member_id}</Descriptions.Item>
                  <Descriptions.Item label="Member Name">{selectedConfig.member_name}</Descriptions.Item>
                  <Descriptions.Item label="Payment Callback URL" span={2}>
                    <code>{selectedConfig.payment_callback_url || '-'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Settlement Callback URL" span={2}>
                    <code>{selectedConfig.settlement_callback_url || '-'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Refund Callback URL" span={2}>
                    <code>{selectedConfig.refund_callback_url || '-'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Callback Enabled">
                    <Tag color={selectedConfig.callback_enabled ? 'green' : 'red'}>
                      {selectedConfig.callback_enabled ? 'Yes' : 'No'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Use Signature">
                    <Tag color={selectedConfig.use_signature ? 'green' : 'red'}>
                      {selectedConfig.use_signature ? 'Yes' : 'No'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Max Retry Attempts">{selectedConfig.max_retry_attempts}</Descriptions.Item>
                  <Descriptions.Item label="Retry Interval">{selectedConfig.retry_interval} seconds</Descriptions.Item>
                  <Descriptions.Item label="Timeout">{selectedConfig.timeout_seconds} seconds</Descriptions.Item>
                  <Descriptions.Item label="Email Notifications">
                    <Tag color={selectedConfig.email_notifications ? 'green' : 'red'}>
                      {selectedConfig.email_notifications ? 'Enabled' : 'Disabled'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="SMS Notifications">
                    <Tag color={selectedConfig.sms_notifications ? 'green' : 'red'}>
                      {selectedConfig.sms_notifications ? 'Enabled' : 'Disabled'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Updated">
                    {new Date(selectedConfig.updated_at).toLocaleString('id-ID')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </TabPane>
            <TabPane tab={`Pending Settlements (${pendingSettlements.filter(p => p.status === 'pending').length})`} key="pending">
              <Card>
                <Table
                  dataSource={pendingSettlements}
                  rowKey="id"
                  size="small"
                  columns={[
                    { title: 'Transaction ID', dataIndex: 'transaction_id', key: 'transaction_id', ellipsis: true },
                    { title: 'Jumlah', dataIndex: 'amount', key: 'amount', render: v => `Rp ${Number(v).toLocaleString('id-ID')}` },
                    { title: 'Rilis Pada', dataIndex: 'release_at', key: 'release_at', render: v => new Date(v).toLocaleString('id-ID') },
                    { title: 'Dibuat', dataIndex: 'created_at', key: 'created_at', render: v => new Date(v).toLocaleString('id-ID') },
                    {
                      title: 'Status', dataIndex: 'status', key: 'status',
                      render: s => <Tag color={s === 'released' ? 'green' : s === 'cancelled' ? 'red' : 'orange'}>
                        {s === 'released' ? 'Dirilis' : s === 'cancelled' ? 'Dibatalkan' : 'Menunggu'}
                      </Tag>
                    },
                    { title: 'Dirilis Pada', dataIndex: 'released_at', key: 'released_at', render: v => v ? new Date(v).toLocaleString('id-ID') : '-' },
                  ]}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                />
              </Card>
            </TabPane>
          </Tabs>
        </div>
      )}

      {/* Settlement Delay Modal */}
      <Modal
        title="Edit Settlement Delay"
        open={settlementModalVisible}
        onCancel={() => setSettlementModalVisible(false)}
        footer={null}
        width={400}
      >
        <Form form={settlementForm} layout="vertical" onFinish={handleSettlementSubmit}>
          <Form.Item
            label="Settlement Delay (jam)"
            name="settlement_delay_hours"
            extra="0 = real-time (langsung tersedia), 24 = H+1, 48 = H+2, dst"
            rules={[{ required: true, type: 'number', min: 0, max: 720 }]}
          >
            <InputNumber min={0} max={720} style={{ width: '100%' }} placeholder="Contoh: 0, 24, 48" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setSettlementModalVisible(false)}>Batal</Button>
              <Button type="primary" htmlType="submit">Simpan</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Callback Configuration"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Payment Callback URL"
            name="payment_callback_url"
          >
            <Input placeholder="https://your-domain.com/webhook/payment" />
          </Form.Item>

          <Form.Item
            label="Settlement Callback URL"
            name="settlement_callback_url"
          >
            <Input placeholder="https://your-domain.com/webhook/settlement" />
          </Form.Item>

          <Form.Item
            label="Refund Callback URL"
            name="refund_callback_url"
          >
            <Input placeholder="https://your-domain.com/webhook/refund" />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Max Retry Attempts"
                name="max_retry_attempts"
                initialValue={3}
              >
                <InputNumber min={1} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Retry Interval (seconds)"
                name="retry_interval"
                initialValue={300}
              >
                <InputNumber min={30} max={3600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Timeout (seconds)"
            name="timeout_seconds"
            initialValue={30}
          >
            <InputNumber min={5} max={120} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Settlement Delay (jam)"
            name="settlement_delay_hours"
            initialValue={0}
            extra="0 = real-time langsung tersedia, 24 = H+1, 48 = H+2, dst"
          >
            <InputNumber min={0} max={720} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Callback Enabled"
                name="callback_enabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Use Signature"
                name="use_signature"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Email Notifications"
                name="email_notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="SMS Notifications"
                name="sms_notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                Update
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Logs Modal */}
      <Modal
        title={`Callback Logs - Member: ${selectedMemberId}`}
        open={logsModalVisible}
        onCancel={() => setLogsModalVisible(false)}
        footer={null}
        width={1200}
      >
        <Input.Search
          allowClear
          enterButton
          placeholder="Cari ID transaksi atau Ref ID mitra"
          value={logSearch}
          onChange={(e) => setLogSearch(e.target.value)}
          onSearch={(v) => fetchCallbackLogs(selectedMemberId, 1, v)}
          style={{ marginBottom: 16, maxWidth: 420 }}
        />
        <Table
          columns={logColumns}
          dataSource={callbackLogs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: logsPagination.current,
            pageSize: logsPagination.pageSize,
            total: logsPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} logs`,
            onChange: (page) => fetchCallbackLogs(selectedMemberId, page)
          }}
          scroll={{ x: 800 }}
        />
      </Modal>

    </div>
  );
};

export default CallbackConfig;